import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Button,
  Snackbar
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../types/users';

// Configuration par défaut des permissions de visibilité
const DEFAULT_VISIBILITY_PERMISSIONS = {
  [USER_ROLES.ADMIN]: {
    sidebar: {
      planning: true,
      statistics: true,
      absences: true,
      technicians: true,
      skills: true,
      settings: true
    },
    tabs: {
      statistics: {
        coverage: true,
        technicians: true,
        analysis: true
      },
      settings: {
        userRights: true,
        emailSettings: true,
        smtpSettings: false, // Réservé aux SuperAdmin
        changePassword: true
      }
    }
  },
  [USER_ROLES.VIEWER]: {
    sidebar: {
      planning: true,
      statistics: false,
      absences: true,
      technicians: false,
      skills: false,
      settings: false
    },
    tabs: {
      statistics: {
        coverage: false,
        technicians: false,
        analysis: false
      },
      settings: {
        userRights: false,
        emailSettings: false,
        smtpSettings: false,
        changePassword: true
      }
    }
  }
};

const SIDEBAR_ITEMS = [
  { key: 'planning', label: 'Planning', description: 'Accès au calendrier des astreintes' },
  { key: 'statistics', label: 'Statistiques', description: 'Accès aux statistiques et rapports' },
  { key: 'absences', label: 'Absences', description: 'Gestion des absences' },
  { key: 'technicians', label: 'Techniciens', description: 'Gestion des techniciens' },
  { key: 'skills', label: 'Compétences', description: 'Gestion des compétences' },
  { key: 'settings', label: 'Paramètres', description: 'Accès aux paramètres' }
];

const TAB_GROUPS = {
  statistics: {
    label: 'Statistiques',
    tabs: [
      { key: 'coverage', label: 'Couverture', description: 'Vue de la couverture par compétence' },
      { key: 'technicians', label: 'Techniciens', description: 'Statistiques des techniciens' },
      { key: 'analysis', label: 'Analyses', description: 'Analyses avancées et graphiques' }
    ]
  },
  settings: {
    label: 'Paramètres',
    tabs: [
      { key: 'userRights', label: 'Droits d\'accès', description: 'Gestion des utilisateurs et droits' },
      { key: 'emailSettings', label: 'Notifications email', description: 'Configuration des notifications' },
      { key: 'smtpSettings', label: 'Paramétrage SMTP', description: 'Configuration serveur SMTP (SuperAdmin uniquement)' },
      { key: 'changePassword', label: 'Mot de passe', description: 'Changement de mot de passe' }
    ]
  }
};

class VisibilityPermissionsDB {
  constructor() {
    this.storageKey = 'visibilityPermissions';
    this.permissions = this.load();
  }

  load() {
    const data = localStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : DEFAULT_VISIBILITY_PERMISSIONS;
  }

  save() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.permissions));
  }

  getPermissions() {
    return this.permissions;
  }

  updatePermissions(permissions) {
    this.permissions = permissions;
    this.save();
  }

  getVisibilityForRole(role) {
    return this.permissions[role] || DEFAULT_VISIBILITY_PERMISSIONS[role];
  }

  updateVisibilityForRole(role, visibility) {
    this.permissions[role] = visibility;
    this.save();
  }
}

const visibilityDB = new VisibilityPermissionsDB();

const PermissionsSettings = () => {
  const { currentUser } = useAuth();
  const [permissions, setPermissions] = useState(DEFAULT_VISIBILITY_PERMISSIONS);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPER_ADMIN;

  useEffect(() => {
    const loadedPermissions = visibilityDB.getPermissions();
    setPermissions(loadedPermissions);
  }, []);

  const handleSidebarChange = (role, itemKey, checked) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        sidebar: {
          ...prev[role].sidebar,
          [itemKey]: checked
        }
      }
    }));
  };

  const handleTabChange = (role, groupKey, tabKey, checked) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        tabs: {
          ...prev[role].tabs,
          [groupKey]: {
            ...prev[role].tabs[groupKey],
            [tabKey]: checked
          }
        }
      }
    }));
  };

  const handleSave = () => {
    visibilityDB.updatePermissions(permissions);
    setShowSnackbar(true);
  };

  const handleReset = () => {
    setPermissions(DEFAULT_VISIBILITY_PERMISSIONS);
    visibilityDB.updatePermissions(DEFAULT_VISIBILITY_PERMISSIONS);
    setShowSnackbar(true);
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Seuls les SuperAdmin peuvent accéder à cette fonctionnalité.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Gestion de la visibilité par rôle
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configurez quels éléments de l'interface sont visibles pour chaque rôle utilisateur.
      </Typography>

      <Grid container spacing={3}>
        {[USER_ROLES.ADMIN, USER_ROLES.VIEWER].map((role) => (
          <Grid item xs={12} md={6} key={role}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Rôle : {role}
                </Typography>

                {/* Éléments de la sidebar */}
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Éléments de la sidebar
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {SIDEBAR_ITEMS.map((item) => (
                  <FormControlLabel
                    key={item.key}
                    control={
                      <Switch
                        checked={permissions[role]?.sidebar?.[item.key] || false}
                        onChange={(e) => handleSidebarChange(role, item.key, e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      width: '100%',
                      mb: 1,
                      '& .MuiFormControlLabel-label': {
                        ml: 1
                      }
                    }}
                  />
                ))}

                {/* Sous-onglets */}
                {Object.entries(TAB_GROUPS).map(([groupKey, group]) => (
                  <Box key={groupKey} sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Sous-onglets : {group.label}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {group.tabs.map((tab) => {
                      const isDisabled = tab.key === 'smtpSettings' && role !== USER_ROLES.SUPER_ADMIN;
                      return (
                        <FormControlLabel
                          key={tab.key}
                          control={
                            <Switch
                              checked={isDisabled ? false : permissions[role]?.tabs?.[groupKey]?.[tab.key] || false}
                              onChange={(e) => handleTabChange(role, groupKey, tab.key, e.target.checked)}
                              size="small"
                              disabled={isDisabled}
                            />
                          }
                          label={
                            <Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: isDisabled ? 'text.disabled' : 'inherit'
                                }}
                              >
                                {tab.label}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color={isDisabled ? 'text.disabled' : 'text.secondary'}
                              >
                                {tab.description}
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start',
                            width: '100%',
                            mb: 1,
                            '& .MuiFormControlLabel-label': {
                              ml: 1
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={handleSave}
          color="primary"
        >
          Sauvegarder les modifications
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleReset}
          color="secondary"
        >
          Réinitialiser aux valeurs par défaut
        </Button>
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        message="Permissions sauvegardées avec succès"
      />
    </Box>
  );
};

export { VisibilityPermissionsDB, visibilityDB };
export default PermissionsSettings; 